/** Max upload size for admin audio (matches server limit). */
export const AUDIO_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/** Format byte size for list/detail display (e.g. 41 KB, 2 MB). */
export function formatAudioBytes(sizeBytes) {
    const b = Number(sizeBytes);
    if (!Number.isFinite(b) || b < 0) return "";
    if (b < 1024) return `${Math.round(b)} B`;
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
    const mb = b / (1024 * 1024);
    if (mb >= 10) return `${Math.round(mb)} MB`;
    const rounded = mb.toFixed(1).replace(/\.0$/, "");
    return `${rounded} MB`;
}

/** Same label normalization as admin `CustomTags` (first letter upper). */
export function normalizeTagLabel(tag) {
    const trimmed = String(tag ?? "").trim();
    if (!trimmed) return "";
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/** Normalize API `tags` to a deduped string array for the autocomplete. */
export function normalizeTagsArray(tags) {
    if (!Array.isArray(tags)) return [];
    const seen = new Set();
    const out = [];
    for (const t of tags) {
        const s = String(t).trim();
        if (!s) continue;
        const key = s.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(s);
    }
    return out;
}

/** API multipart `tags` field: comma-separated string. */
export function tagsToCommaInput(tags) {
    return normalizeTagsArray(tags).join(", ");
}

function extensionFromPath(source) {
    const path = String(source || "").split(/[?#]/)[0];
    const name = path.slice(path.lastIndexOf("/") + 1).toLowerCase();
    const dot = name.lastIndexOf(".");
    if (dot < 0 || dot === name.length - 1) return "";
    return name.slice(dot + 1);
}

function extensionFromContentType(contentType) {
    const ct = String(contentType || "").toLowerCase();
    if (ct.includes("mpeg") || ct.includes("mp3")) return "mp3";
    if (ct.includes("wav")) return "wav";
    if (ct.includes("caf") || ct.includes("coreaudio")) return "caf";
    if (ct.includes("mp4") || ct.includes("m4a") || ct.includes("aac")) return "m4a";
    return "";
}

function basenameWithoutExtension(filename) {
    const raw = String(filename || "").trim();
    if (!raw) return "";
    const name = raw.split(/[/\\]/).pop() || raw;
    const dot = name.lastIndexOf(".");
    if (dot <= 0) return name;
    return name.slice(0, dot);
}

/**
 * Download name from original upload basename + stored asset extension
 * (e.g. Smb_….wav → Smb_….mp3). Avoids UUID S3 keys.
 */
export function buildAudioDownloadFilename(row) {
    const original = String(row?.originalFilename || "").trim();
    const displayName = String(row?.name || "").trim();
    const base =
        basenameWithoutExtension(original)
        || basenameWithoutExtension(displayName)
        || "audio";
    const ext =
        extensionFromPath(row?.s3Key || row?.url)
        || extensionFromContentType(row?.contentType)
        || extensionFromPath(original)
        || "mp3";
    const safeBase = base.replace(/[\\/:*?"<>|]+/g, "_").trim() || "audio";
    return `${safeBase}.${ext}`;
}

/** Fetch audio URL and save with {@link buildAudioDownloadFilename}. */
export async function downloadAudioAsset(row) {
    const url = String(row?.url || "").trim();
    if (!url) {
        throw new Error("No audio URL to download");
    }
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = buildAudioDownloadFilename(row);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
}

/** Upload accepts MP3, WAV, or Apple Core Audio Format (CAF). */
export function isAcceptedUploadAudioFile(file) {
    if (!file) return false;
    const name = (file.name || "").toLowerCase();
    const type = (file.type || "").toLowerCase();
    const isMp3 =
        name.endsWith(".mp3") ||
        type === "audio/mpeg" ||
        type === "audio/mp3" ||
        type === "application/octet-stream";
    const isWav =
        name.endsWith(".wav") || type === "audio/wav" || type === "audio/wave" || type === "audio/x-wav";
    const isCaf =
        name.endsWith(".caf") ||
        type === "audio/x-caf" ||
        type === "audio/caf" ||
        type === "audio/x-coreaudio";
    return isMp3 || isWav || isCaf;
}

/** `null` when valid; otherwise a short message for the user. */
export function getAudioUploadValidationError(file) {
    if (!file) return null;
    if (!isAcceptedUploadAudioFile(file)) {
        return "Only MP3, WAV, or CAF files are accepted.";
    }
    if (file.size > AUDIO_UPLOAD_MAX_BYTES) {
        return `File must be ${formatAudioBytes(AUDIO_UPLOAD_MAX_BYTES)} or smaller.`;
    }
    return null;
}
